import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import { EmptyRow, Section } from "@/components/manage/Section";
import { PageHeader } from "@/components/manage/PageHeader";
import { StatStrip } from "@/components/manage/StatStrip";
import { useActiveOrgId } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/objectives/")({
  component: ObjectivesPage,
});

interface SpaceSummary {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  workspace_id: string;
  member_count: number;
  updated_at: string;
}

function ObjectivesPage() {
  const api = useApi();
  const orgId = useActiveOrgId();

  const objectives = useQuery({
    queryKey: ["admin-objectives", orgId],
    queryFn: () => api.request<SpaceSummary[]>(`/admin/orgs/${orgId}/objectives`),
  });

  const total = objectives.data?.length ?? 0;
  const withMembers = objectives.data?.filter((o) => o.member_count > 0).length ?? 0;

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Plan access"
        title="Objectives"
        description="Grant users access to objectives. Access is independent of the home workspace."
      />

      <StatStrip
        items={[
          { label: "Total", value: total, hint: "in this org" },
          { label: "Shared", value: withMembers, hint: "have members" },
          { label: "Scope", value: "people", hint: "no datasets" },
        ]}
      />

      <Section title="All objectives">
        {objectives.isLoading ? (
          <Skeleton className="h-40 w-full rounded-md" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-soft)]">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Name
                  </th>
                  <th className="w-44 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Slug
                  </th>
                  <th className="w-28 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Members
                  </th>
                  <th className="w-32 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                {objectives.data?.length ? (
                  objectives.data.map((o) => (
                    <tr
                      key={o.id}
                      className="group transition hover:bg-[var(--color-surface)]"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to="/manage/objectives/$id"
                          params={{ id: o.id }}
                          className="inline-flex items-center gap-2 font-medium text-foreground hover:text-[var(--color-primary)]"
                        >
                          {o.name}
                          <ArrowUpRight className="size-3 opacity-0 transition group-hover:opacity-60" />
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <code className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                          /{o.slug}
                        </code>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                        {o.member_count}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {new Date(o.updated_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow colSpan={4}>
                    No objectives yet — create one from the app's Plan view.
                  </EmptyRow>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
